import { supabase } from '../lib/supabase';
import {
  ProductIssueReport,
  ProductIssueReportWithProduct,
  CreateIssueReportInput,
  UpdateIssueReportInput,
  ReportStatus,
} from '../types/issueReport';

/**
 * Create a new product issue report
 */
export async function createIssueReport(
  input: CreateIssueReportInput
): Promise<ProductIssueReport | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('product_issue_reports')
      .insert({
        user_id: user.id,
        user_product_id: input.user_product_id,
        issue_type: input.issue_type,
        reported_field: input.reported_field,
        current_value: input.current_value,
        suggested_value: input.suggested_value,
        description: input.description,
        priority: input.priority || 'medium',
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating issue report:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Failed to create issue report:', error);
    return null;
  }
}

/**
 * Get all issue reports for the current user
 */
export async function getUserIssueReports(): Promise<ProductIssueReportWithProduct[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('product_issue_reports')
      .select(`
        *,
        user_product:user_products!inner(
          id,
          valuation,
          condition,
          images,
          variant:product_variants!inner(
            id,
            attributes,
            model:product_models!inner(
              id,
              name,
              family:product_families!inner(
                id,
                name,
                brand:brands!inner(
                  id,
                  name
                )
              )
            )
          )
        )
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user issue reports:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch user issue reports:', error);
    return [];
  }
}

/**
 * Get a single issue report by ID
 */
export async function getIssueReportById(
  reportId: string
): Promise<ProductIssueReportWithProduct | null> {
  try {
    const { data, error } = await supabase
      .from('product_issue_reports')
      .select(`
        *,
        user_product:user_products!inner(
          id,
          valuation,
          condition,
          images,
          variant:product_variants!inner(
            id,
            attributes,
            model:product_models!inner(
              id,
              name,
              family:product_families!inner(
                id,
                name,
                brand:brands!inner(
                  id,
                  name
                )
              )
            )
          )
        )
      `)
      .eq('id', reportId)
      .single();

    if (error) {
      console.error('Error fetching issue report:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Failed to fetch issue report:', error);
    return null;
  }
}

/**
 * Update an issue report (admin function)
 */
export async function updateIssueReport(
  reportId: string,
  updates: UpdateIssueReportInput
): Promise<ProductIssueReport | null> {
  try {
    const { data, error } = await supabase
      .from('product_issue_reports')
      .update(updates)
      .eq('id', reportId)
      .select()
      .single();

    if (error) {
      console.error('Error updating issue report:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Failed to update issue report:', error);
    return null;
  }
}

/**
 * Get all issue reports (admin function)
 * Note: This requires service role or admin privileges
 */
export async function getAllIssueReports(
  status?: ReportStatus
): Promise<ProductIssueReportWithProduct[]> {
  try {
    let query = supabase
      .from('product_issue_reports')
      .select(`
        *,
        user_product:user_products!inner(
          id,
          valuation,
          condition,
          images,
          variant:product_variants!inner(
            id,
            attributes,
            model:product_models!inner(
              id,
              name,
              family:product_families!inner(
                id,
                name,
                brand:brands!inner(
                  id,
                  name
                )
              )
            )
          )
        )
      `);

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching all issue reports:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch all issue reports:', error);
    return [];
  }
}

/**
 * Delete an issue report (user can delete their own pending reports)
 */
export async function deleteIssueReport(reportId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('product_issue_reports')
      .delete()
      .eq('id', reportId);

    if (error) {
      console.error('Error deleting issue report:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Failed to delete issue report:', error);
    return false;
  }
}

/**
 * Get issue report statistics (admin function)
 */
export async function getIssueReportStats(): Promise<{
  total: number;
  pending: number;
  in_review: number;
  resolved: number;
  rejected: number;
}> {
  try {
    const { data, error } = await supabase
      .from('product_issue_reports')
      .select('status');

    if (error) {
      console.error('Error fetching issue report stats:', error);
      throw error;
    }

    const stats = {
      total: data?.length || 0,
      pending: 0,
      in_review: 0,
      resolved: 0,
      rejected: 0,
    };

    data?.forEach((report) => {
      if (report.status === 'pending') stats.pending++;
      else if (report.status === 'in_review') stats.in_review++;
      else if (report.status === 'resolved') stats.resolved++;
      else if (report.status === 'rejected') stats.rejected++;
    });

    return stats;
  } catch (error) {
    console.error('Failed to fetch issue report stats:', error);
    return { total: 0, pending: 0, in_review: 0, resolved: 0, rejected: 0 };
  }
}
